import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Flag, Search, LineChart, FileText, Palette, Globe, Megaphone, Rocket,
  ChevronDown, ArrowRight, FileDown, Loader2, Users,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Seo } from "@/lib/seo";

// Each outcome expands to a plain-English description; where it maps to a real
// part of the platform we add a "Learn more" link (honest, no overclaiming).
const OUTCOMES = [
  { t: "A validated business idea — built around your skills, your passion, or a market opportunity",
    d: "You don't have to force a business around only your military job. We help you weigh three honest paths — what you're good at, what you love, and where there's real demand — and validate the idea before you invest time or money.",
    link: "/start", linkLabel: "Start the free assessment" },
  { t: "A lender-ready business plan",
    d: "A professional plan in the format banks, the SBA and investors expect — with AI-drafted sections you edit, 3-year financials, and matched grant & funding leads.",
    link: "/business-plan", linkLabel: "See the Business Plan" },
  { t: "A registered business with a professional brand",
    d: "Guidance on registering your business the right way, plus a logo, colors, fonts and voice so you look established from day one." },
  { t: "A live, professionally designed website",
    d: "A fast, mobile-friendly website built for you — the front door customers judge you by.",
    link: "/services", linkLabel: "View website packages" },
  { t: "A Google Business Profile + social media presence",
    d: "Set up on Google and the social platforms that fit your business, so customers can find and trust you." },
  { t: "Business email, marketing materials & QR codes",
    d: "A professional email address, print-ready materials and QR codes so you can start marketing the moment you launch." },
  { t: "An AI Business Strategist (Top Foster) in your corner",
    d: "Personalized, plain-English guidance whenever you need it — recommendations, copy and next steps tailored to your business.",
    link: "/ai-concierge", linkLabel: "Meet Top Foster" },
  { t: "A practical 12-month growth roadmap",
    d: "A clear month-by-month plan of what to do next after launch, so momentum doesn't stall." },
];

const WEEKS = [
  { n: "1", icon: Flag, title: "Identity", d: "Who do you want to become after the military? We map your skills, interests and vision." },
  { n: "2", icon: Search, title: "Business Discovery", d: "A guided assessment translates your military experience into real business options with AI recommendations." },
  { n: "3", icon: LineChart, title: "Business Validation", d: "Market research, competitor analysis, pricing, your ideal customer and a simple SWOT — proof the idea holds up." },
  { n: "4", icon: FileText, title: "Business Plan", d: "Mission & vision, financials, marketing, funding, operations and growth — your full plan takes shape." },
  { n: "5", icon: Palette, title: "Brand Development", d: "Your logo, color palette, fonts, voice and tagline — a brand that looks established." },
  { n: "6", icon: Globe, title: "Website", d: "Your website goes live with contact forms, SEO basics, booking and lead capture." },
  { n: "7", icon: Megaphone, title: "Marketing", d: "Google Business Profile, Facebook/Instagram/LinkedIn, YouTube/TikTok and email marketing set up." },
  { n: "8", icon: Rocket, title: "Launch Week", d: "Website & socials live, business cards & QR codes, business email, analytics and CRM — you launch." },
];

export default function CohortOverview() {
  const [open, setOpen] = useState(0);
  const [dl, setDl] = useState(false);

  const downloadPdf = async () => {
    setDl(true);
    try {
      const r = await api.get("/cohort/one-pager", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "operation-second-mission-overview.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download — please try again."); } finally { setDl(false); }
  };

  return (
    <div data-testid="cohort-overview-page">
      <Seo title="Operation Second Mission — Program Overview | ATF WebWorks"
        description="An 8-week launch program for transitioning service members. Explore what every participant leaves with and the week-by-week process." path="/veterans/cohort/overview" />

      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden grain">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-20 relative">
          <Link to="/veterans/cohort" data-testid="overview-back" className="text-gray-400 hover:text-gold text-xs mb-5 inline-block transition-colors">← Bring a cohort</Link>
          <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-4"><ShieldCheck size={15} /> Operation Second Mission</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-white leading-[1.05] max-w-3xl">
            Leave the service with a <span className="text-gold">launched business</span> — not just a DD-214.
          </h1>
          <p className="text-base md:text-lg text-gray-300 font-light mt-6 max-w-2xl leading-relaxed">
            An 8-week program for transitioning service members. Tap any outcome or week below to see exactly what it means.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <Link to="/veterans/cohort" data-testid="overview-request-cta" className="inline-flex items-center gap-2 bg-gold text-navy px-7 py-3.5 text-sm font-semibold hover:bg-gold-light transition-colors">
              Bring a cohort to your program <ArrowRight size={16} />
            </Link>
            <button onClick={downloadPdf} disabled={dl} data-testid="overview-download-pdf"
              className="inline-flex items-center gap-2 text-sm border border-white/15 text-white px-5 py-3 hover:border-gold/50 transition-colors disabled:opacity-60">
              {dl ? <Loader2 className="animate-spin" size={15} /> : <FileDown size={15} />} Download PDF handout
            </button>
          </div>
        </div>
      </section>

      {/* Outcomes — interactive accordion */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-16">
        <h2 className="font-heading text-3xl text-white mb-2">What every participant leaves with</h2>
        <p className="text-gray-400 mb-8">Tap a line to see what it means.</p>
        <div className="border border-white/10 divide-y divide-white/10" data-testid="overview-outcomes">
          {OUTCOMES.map((o, i) => {
            const isOpen = open === i;
            return (
              <div key={i} data-testid={`overview-outcome-${i}`}>
                <button onClick={() => setOpen(isOpen ? -1 : i)} data-testid={`overview-outcome-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/[0.03] transition-colors">
                  <span className="flex items-center gap-3 text-white text-sm md:text-base">
                    <ShieldCheck size={16} className="text-gold shrink-0" /> {o.t}
                  </span>
                  <ChevronDown size={18} className={`text-gold shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                    className="px-5 pb-5 pl-12" data-testid={`overview-outcome-desc-${i}`}>
                    <p className="text-gray-400 text-sm leading-relaxed">{o.d}</p>
                    {o.link && (
                      <Link to={o.link} data-testid={`overview-outcome-link-${i}`}
                        className="inline-flex items-center gap-1.5 text-gold text-sm font-semibold mt-3 hover:underline">
                        {o.linkLabel} <ArrowRight size={14} />
                      </Link>
                    )}
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* 8-week process */}
      <section className="border-y border-white/10 bg-navy-800/40">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16">
          <h2 className="font-heading text-3xl text-white mb-2">The 8-week process</h2>
          <p className="text-gray-400 mb-8">From identity to launch, one week at a time.</p>
          <div className="grid md:grid-cols-2 gap-4" data-testid="overview-weeks">
            {WEEKS.map((w) => (
              <div key={w.n} className="border border-white/10 bg-navy-800 p-6 flex gap-4" data-testid={`overview-week-${w.n}`}>
                <div className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold shrink-0"><w.icon size={20} /></div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gold mb-0.5">Week {w.n}</div>
                  <h3 className="font-heading text-lg text-white mb-1.5">{w.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{w.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-16 text-center">
        <div className="inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-4"><Users size={15} /> Built for cohorts</div>
        <h2 className="font-heading text-3xl text-white mb-3">Bring Operation Second Mission to your installation</h2>
        <p className="text-gray-400 max-w-2xl mx-auto mb-7">One facilitator can support an entire cohort — the AI strategist guides each participant individually. Simple onboarding for transition offices, VSOs and resource partners.</p>
        <Link to="/veterans/cohort" data-testid="overview-bottom-cta" className="inline-flex items-center gap-2 bg-gold text-navy px-8 py-3.5 text-sm font-semibold hover:bg-gold-light transition-colors">
          Request a call <ArrowRight size={16} />
        </Link>
      </section>
    </div>
  );
}
